import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, MapPin, Globe, TrendingUp, Phone, Users, Star, ArrowRight,
  Briefcase, Calendar, FileText, Plus, Search,
} from "lucide-react";
import api from "../lib/api";
import DiscoverProspectsDialog from "../components/DiscoverProspectsDialog";

const PHASE_HOT = ["callout_quote", "design_quote", "estimate_sent", "so_sent"];

export default function SalesFunnel() {
  const [stats, setStats] = useState(null);
  const [hotLeads, setHotLeads] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [activeTrips, setActiveTrips] = useState([]);
  const [discoverOpen, setDiscoverOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [leadsR, tripsR] = await Promise.all([
          api.get("/leads"),
          api.get("/trips"),
        ]);
        const leads = leadsR.data || [];
        const trips = tripsR.data || [];
        // Stats
        const open = leads.filter((l) => !["won", "lost"].includes(l.phase)).length;
        const hotCount = leads.filter((l) => PHASE_HOT.includes(l.phase)).length;
        const overdueFollowups = leads.filter((l) => {
          if (!l.next_followup_date) return false;
          return new Date(l.next_followup_date).getTime() < Date.now();
        }).length;
        const tripsActive = trips.filter((t) => t.status === "active").length;
        setStats({ openLeads: open, hotPhase: hotCount, overdueFollowups, tripsActive, totalLeads: leads.length });
        // Hot leads (top 10 by heat_score, hot phase priority)
        setHotLeads([...leads]
          .filter((l) => !["won", "lost"].includes(l.phase))
          .sort((a, b) => {
            const ha = (PHASE_HOT.includes(a.phase) ? 100 : 0) + Number(a.heat_score || 0);
            const hb = (PHASE_HOT.includes(b.phase) ? 100 : 0) + Number(b.heat_score || 0);
            return hb - ha;
          })
          .slice(0, 10));
        // Active trips for quick resume
        setActiveTrips(trips.filter((t) => t.status === "active" || t.status === "draft").slice(0, 5));
      } catch (_e) { /* noop */ }
      try {
        // Sales briefs are stored under sales_briefs and we don't currently expose
        // a list endpoint — derive from leads that have a `place_id` (proxy for "researchable")
        // and quietly fetch any cached briefs. For now, leave briefs empty and we'll
        // wire a /api/research/briefs list later if the user wants.
        setBriefs([]);
      } catch (_e) { /* noop */ }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="sales-funnel-page">
      {/* Hero */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#1FB6C1] flex items-center gap-2">
          <TrendingUp className="w-3 h-3" />
          Sales Funnel
        </div>
        <h1 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-1">
          Your sales command centre
        </h1>
        <p className="text-sm text-[#a0aabf] mt-2 max-w-2xl">
          Plan trips, research prospects deeply, discover new leads, and triage your hottest opportunities — all in one place.
        </p>
      </div>

      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sales-funnel-stats">
          <StatCard label="Open leads" value={stats.openLeads} sub={`of ${stats.totalLeads} total`} color="#1FB6C1" />
          <StatCard label="Hot phase" value={stats.hotPhase} sub="Callout / Design / Estimate / SO" color="#F59E0B" />
          <StatCard label="Overdue follow-ups" value={stats.overdueFollowups} sub="Need attention" color="#EF4444" />
          <StatCard label="Trips active" value={stats.tripsActive} sub="On the road or planning" color="#A78BFA" />
        </div>
      )}

      {/* Tools grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="sales-funnel-tools">
        <ToolCard
          to="/trips"
          icon={MapPin}
          color="#1FB6C1"
          title="Plan a trip"
          desc="Build itineraries with verified addresses, AI sales briefs, and one-click activation into your calendar."
          testId="tool-card-trips"
        />
        <ToolCard
          to="/suspects"
          icon={Globe}
          color="#A78BFA"
          title="Prospects"
          desc="Browse every suspect/prospect on your radar. Verify, enrich, or promote to leads."
          testId="tool-card-prospects"
        />
        <ToolCard
          onClick={() => setDiscoverOpen(true)}
          icon={Sparkles}
          color="#10B981"
          title="Discover new"
          desc="Find new lodges, hotels, restaurants near any area via Google Places. Add as suspects in one tap."
          testId="tool-card-discover"
        />
        <ToolCard
          to="/leads"
          icon={Briefcase}
          color="#F59E0B"
          title="Leads pipeline"
          desc="Excel-grade table view with multi-sort, filters, multi-row actions, and Hidro-suggested next moves."
          testId="tool-card-pipeline"
        />
        <ToolCard
          to="/research"
          icon={FileText}
          color="#EC4899"
          title="Deep research"
          desc="AI sales briefs combining Google Places, website scrape, Wayback history, and your internal call/comment history."
          testId="tool-card-research"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Hottest leads this week */}
        <div className="hv-card p-4 min-w-0" data-testid="sales-funnel-hot">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-bold flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-300" />
              Hottest leads
            </h2>
            <Link to="/leads" className="text-[10px] text-[#1FB6C1] hover:underline">View all →</Link>
          </div>
          {hotLeads.length === 0 ? (
            <p className="text-xs text-[#647087]">No active leads yet. Try Discover prospects.</p>
          ) : (
            <ul className="space-y-1.5">
              {hotLeads.map((l) => (
                <li key={l.id} className="flex items-center gap-2 text-sm min-w-0" data-testid={`sales-funnel-hot-${l.id}`}>
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#1fb6c1]/15 text-[#1fb6c1] font-mono shrink-0">
                    {l.phase?.replace("_", " ")}
                  </span>
                  <Link to={`/leads/${l.id}`} className="flex-1 min-w-0 hover:text-[#1FB6C1] truncate font-bold">
                    {l.customer_name || l.title || "(unnamed lead)"}
                  </Link>
                  <span className="text-[10px] text-[#647087] font-mono shrink-0">
                    {l.province_area || "—"}
                  </span>
                  <Link
                    to={`/research/lead/${l.id}?from=sales-funnel`}
                    className="text-[#A78BFA] hover:text-[#1FB6C1] shrink-0"
                    title="Deep research"
                    data-testid={`sales-funnel-research-${l.id}`}
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Active / draft trips */}
        <div className="hv-card p-4 min-w-0" data-testid="sales-funnel-trips">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-bold flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#1FB6C1]" />
              Your trips
            </h2>
            <Link to="/trips" className="text-[10px] text-[#1FB6C1] hover:underline">View all →</Link>
          </div>
          {activeTrips.length === 0 ? (
            <div className="text-xs text-[#647087]">
              No trips yet.{" "}
              <Link to="/trips" className="text-[#1FB6C1] hover:underline">Plan your first trip →</Link>
            </div>
          ) : (
            <ul className="space-y-1.5">
              {activeTrips.map((t) => (
                <li key={t.id} className="flex items-center gap-2 text-sm min-w-0" data-testid={`sales-funnel-trip-${t.id}`}>
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-mono shrink-0 ${t.status === "active" ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-[#a0aabf]"}`}>
                    {t.status}
                  </span>
                  <Link to={`/trips/${t.id}?from=sales-funnel`} className="flex-1 min-w-0 hover:text-[#1FB6C1] truncate">
                    {t.title}
                  </Link>
                  <span className="text-[10px] text-[#647087] font-mono shrink-0">
                    {t.start_date}
                  </span>
                  <span className="text-[10px] text-[#a0aabf] shrink-0">
                    {(t.stops || []).length} stop{(t.stops || []).length === 1 ? "" : "s"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {discoverOpen && (
        <DiscoverProspectsDialog
          onClose={() => setDiscoverOpen(false)}
          onCommitted={() => { /* page will pick up next refresh */ }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, sub, color }) {
  return (
    <div className="hv-card p-4" style={{ borderColor: `${color}40` }}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#647087]">{label}</div>
      <div className="font-display text-3xl font-bold mt-1" style={{ color }}>{value}</div>
      <div className="text-[10px] text-[#a0aabf] mt-1">{sub}</div>
    </div>
  );
}

function ToolCard({ to, onClick, icon: Icon, color, title, desc, testId }) {
  const inner = (
    <>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-5 h-5" style={{ color }} />
        <h3 className="font-display text-base font-bold">{title}</h3>
      </div>
      <p className="text-[11px] text-[#a0aabf] leading-relaxed">{desc}</p>
      <div className="mt-3 text-[10px] uppercase tracking-wider flex items-center gap-1" style={{ color }}>
        Open <ArrowRight className="w-3 h-3" />
      </div>
    </>
  );
  const className = "hv-card p-4 text-left hover:bg-white/[0.02] transition group cursor-pointer";
  if (to) return <Link to={to} className={className} data-testid={testId} style={{ borderColor: `${color}30` }}>{inner}</Link>;
  return <button type="button" onClick={onClick} className={className} data-testid={testId} style={{ borderColor: `${color}30` }}>{inner}</button>;
}
